/**
 * Route guard.
 *
 * This is a convenience, not a security control: it hides screens, it does
 * not protect data. Every endpoint behind it enforces its own session check
 * server-side (§10). If that ever stops being true, this component will not
 * save you.
 */

import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider.js';
import { PrivacyConsent } from '../components/PrivacyConsent.js';

export function RequireAuth({ children }: { children: ReactNode }) {
  const { status, session } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <main className="centered" aria-busy="true">
        <p className="muted">Checking your session…</p>
      </main>
    );
  }

  if (status === 'signed_out') {
    // `replace` so the back button does not return to a guarded route, and
    // `state` so we can come back to where they were aiming after sign-in.
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // The current Privacy Policy, accepted before anything else (0065, C-25).
  // Only an explicit `false` asks: the API always sends the field, and the
  // tests' hand-made sessions, which omit it, are not asking about consent.
  if (session?.privacy && !session.privacy.accepted) {
    return <PrivacyConsent version={session.privacy.version} />;
  }

  return <>{children}</>;
}
